export interface Neighborhood {
  slug: string;
  name: string;
  /** How the place is written in prose when it needs a qualifier, e.g. "Belle Meade". */
  zip: string[];
  blurb: string;
  metaTitle: string;
  metaDescription: string;
  intro: string;
  /** Long-form, location-specific body copy. Each section is a H2 plus paragraphs. */
  sections: Array<{ heading: string; paragraphs: string[] }>;
  highlights: string[];
  landmarks: string[];
  responseTime: string;
}

const PRICING_PARAGRAPH =
  "Care is offered two ways: monthly plans that bundle a standing weekly schedule with coordination, and hourly care for occasional or additional visits. Concierge covers coordination only, without in-home visits, while the in-home plans add one, two, or more standing visits each week with priority for weekend and overnight requests. Current package pricing is listed on our services page. Every plan is non-medical and private-pay, and we confirm the schedule and total in writing after a private consultation.";

export const neighborhoods: Neighborhood[] = [
  {
    slug: "nashville",
    name: "Nashville",
    zip: ["37203", "37205", "37212", "37215", "37220"],
    blurb: "Concierge, non-medical companion care for older adults across Nashville, TN.",
    metaTitle: "Companion Care & Elder Care in Nashville, TN | Kinship Care",
    metaDescription:
      "Kinship Care Collective provides private companion care, elder care and in-home senior care across Nashville, TN — companionship, transportation and respite. Non-medical.",
    intro:
      "Kinship Care Collective is a Nashville companion care and elder care practice built for older adults who intend to stay in the homes they know. We are non-medical by design: companionship, transportation, meals shared rather than dropped off, and the steady presence that turns a long week into an ordinary one.",
    sections: [
      {
        heading: "A Nashville practice, not a call center",
        paragraphs: [
          "Nashville is not one city so much as a collection of neighborhoods with their own rhythms — Green Hills and its standing hair appointments, Belle Meade's quiet boulevards, East Nashville's porches, the walkable blocks of 12 South and Germantown. A companion who knows the difference between a Tuesday morning at the Richland Park Farmers' Market and a Saturday at the Nashville Farmers' Market is a companion who can build a good day rather than fill a shift.",
          "Every engagement here is founder-led. You speak with the person who will choose your companion, and that person stays reachable. There is no regional office, no rotating scheduler, and no roster of strangers cycling through the front door. It is the reason our families describe the arrangement as matched, never assigned.",
        ],
      },
      {
        heading: "What a week of care looks like in Nashville",
        paragraphs: [
          "Most families begin with one standing visit and grow from there. A companion arrives at the same hour on the same day, and the visit has a shape: conversation, a walk at Centennial Park or along the Shelby Bottoms Greenway, a shared lunch made in the kitchen rather than ordered in, a load of laundry folded while the coffee finishes.",
          "Transportation is the second reason families call. We drive to appointments at Vanderbilt University Medical Center, Saint Thomas West, and the specialists tucked around Midtown — and we stay through the appointment so that what the doctor actually said gets written down and shared with the family that evening. Errands, the pharmacy, church, the grocery, and the standing lunch with an old friend all count as care.",
        ],
      },
      {
        heading: "Where families use us most",
        paragraphs: [
          "Adult children living out of state lean on us for eyes in the house and a written note after every visit. Spouses caring for a partner use us for respite — four unhurried hours to leave the house without listening for a fall. Older adults arranging their own support call us because they want company and a ride, not supervision.",
          "We coordinate alongside home health, hospice, and physicians rather than replacing them. When a clinical need appears, we say so plainly and help the family reach the right provider.",
        ],
      },
      {
        heading: "Pricing for Nashville families",
        paragraphs: [PRICING_PARAGRAPH],
      },
    ],
    highlights: [
      "Companionship visits with a consistent, matched companion",
      "Transportation to Vanderbilt, Saint Thomas West, and neighborhood errands",
      "Medication reminders and steady daily routines",
      "Light housekeeping and cook-together kitchen time",
      "Respite for family caregivers, including weekends and overnights",
    ],
    landmarks: ["Green Hills", "Belle Meade", "12 South", "Germantown", "East Nashville", "Centennial Park", "The Gulch"],
    responseTime: "Same-week introductions are typical across Davidson County.",
  },
  {
    slug: "brentwood",
    name: "Brentwood",
    zip: ["37027"],
    blurb: "Private-pay companion care for older adults in Brentwood, TN.",
    metaTitle: "Home Care Brentwood TN | Companion & Elder Care | Kinship Care",
    metaDescription:
      "Non-medical home care in Brentwood, TN: one matched companion for your parent — company, rides to Williamson Medical, meals and respite. Same-week starts in 37027.",
    intro:
      "Kinship Care Collective serves Brentwood families who want home care and elder care support without an agency atmosphere arriving with it. Our work is non-medical and personal: company, transportation, and the quiet logistics that keep an independent life intact.",
    sections: [
      {
        heading: "Care that fits how Brentwood lives",
        paragraphs: [
          "Brentwood households are typically large, set back from the road, and organized around routines built over decades — the same walking loop at Crockett Park, the same table at a Maryland Farms lunch, the same Sunday service. Care that ignores those routines gets politely declined. Care that protects them gets welcomed.",
          "So we begin with the calendar you already keep. A companion works around the standing appointments rather than through them, and we hold the same day and hour week to week so the arrangement becomes ordinary rather than an event.",
        ],
      },
      {
        heading: "Driving, appointments, and the Cool Springs run",
        paragraphs: [
          "Giving up the car is often the hardest conversation a Brentwood family has. Reliable transportation makes it survivable. We drive to Williamson Medical Center, to Vanderbilt clinics up I-65, to Cool Springs for the errands that used to take an afternoon, and to the appointments no one wants to reschedule again.",
          "Our companions stay for the visit and send a short, plain-language note afterward, so the daughter in Denver knows what the cardiologist said before dinner.",
        ],
      },
      {
        heading: "Respite for the spouse or adult child doing it all",
        paragraphs: [
          "A great many Brentwood clients started as a phone call from someone who had not left the house alone in three weeks. Respite is not a luxury in that situation; it is maintenance. Four hours of reliable coverage buys a doctor's appointment of your own, a long lunch, or an afternoon that belongs to you.",
          "Every companion is personally vetted, background-checked, and reference-verified before entering a home, and matched to the household rather than dispatched to it.",
        ],
      },
      {
        heading: "Brentwood pricing",
        paragraphs: [PRICING_PARAGRAPH],
      },
    ],
    highlights: [
      "Companionship visits and daily check-ins across 37027",
      "Transportation to Williamson Medical Center and Cool Springs errands",
      "Medication reminders and daily routines",
      "Light housekeeping and cook-together kitchen time",
      "Help with technology and staying connected with family",
    ],
    landmarks: ["Maryland Farms", "Crockett Park", "Cool Springs", "Concord Road", "Old Hickory Blvd"],
    responseTime: "Same-week placements available across 37027.",
  },
  {
    slug: "franklin",
    name: "Franklin",
    zip: ["37064", "37067", "37069"],
    blurb: "Non-medical companion care for Franklin, TN families and older adults.",
    metaTitle: "Home Care Franklin TN | Companion & Elder Care | Kinship Care",
    metaDescription:
      "Kinship Care Collective provides home care, companion care and elder care for seniors in Franklin, TN — companionship, transportation, respite and overnight support. Non-medical.",
    intro:
      "Kinship Care Collective supports Franklin families with non-medical home care and companion care shaped around the life already being lived — Main Street mornings, church on Sunday, grandchildren on Saturdays, and a house someone intends to stay in.",
    sections: [
      {
        heading: "Franklin, specifically",
        paragraphs: [
          "Franklin rewards getting out of the house, and our companions plan for it. A visit might mean a slow walk through Pinkerton Park, a coffee downtown before the sidewalks fill, a browse at The Factory, or a drive out toward Leiper's Fork when the leaves turn. Movement, conversation, and daylight do more for an older adult's week than any checklist.",
          "For clients in Westhaven and Berry Farms, where neighbors are close and the streets are walkable, care often looks like a companion who makes the neighborhood usable again — walking with someone whose balance has changed, so a short loop stops feeling risky.",
        ],
      },
      {
        heading: "Appointments, errands, and coordination",
        paragraphs: [
          "We drive to Williamson Medical Center, Vanderbilt Health clinics in Cool Springs, and the dentists, dermatologists, and physical therapists scattered across town. We wait, we listen, and we write down what was said.",
          "Between appointments, care covers the ordinary work that quietly gets skipped: prescriptions picked up, the refrigerator restocked, mail sorted, a meal cooked together and portioned for later.",
        ],
      },
      {
        heading: "Overnight and weekend support",
        paragraphs: [
          "Franklin families often need coverage precisely when help is hardest to find. Weekend visits and overnight support are available in the Plus and Premium packages, including the stretch after a hospital discharge when the house feels unsteady and no one wants to leave a parent alone at 2 a.m.",
          "Because we are non-medical, we coordinate with home health and hospice rather than duplicating them — and we say so honestly when a clinical need is outside our scope.",
        ],
      },
      {
        heading: "Franklin pricing",
        paragraphs: [PRICING_PARAGRAPH],
      },
    ],
    highlights: [
      "Outings to Downtown Franklin, The Factory, and local parks",
      "Rides to Williamson Medical Center and Vanderbilt Health",
      "Personalized companion care matched to your loved one",
      "Respite care for family caregivers",
      "Overnight and weekend support available",
    ],
    landmarks: ["Downtown Franklin", "Westhaven", "Berry Farms", "The Factory", "Pinkerton Park"],
    responseTime: "Caregiver matching typically within 48–72 hours.",
  },
  {
    slug: "belle-meade",
    name: "Belle Meade",
    zip: ["37205"],
    blurb: "Discreet, private-pay companion care for older adults in Belle Meade, TN.",
    metaTitle: "In-Home Companion Care in Belle Meade, TN | Private Senior Care",
    metaDescription:
      "Discreet private senior care at home in Belle Meade, TN — in-home companion care, driving, household coordination and respite. Non-medical. Private pay.",
    intro:
      "Kinship Care Collective serves Belle Meade with quiet, non-medical companion care for older adults who value privacy as much as support. One matched companion, one point of contact, and a household that keeps running the way it always has.",
    sections: [
      {
        heading: "Discretion is part of the service",
        paragraphs: [
          "Belle Meade families rarely want an agency presence in the house. They want one person, well chosen, who understands the household's standards and does not need to be managed. That is the practice we built: a single matched companion, a single point of contact, and no rotating roster of unfamiliar faces at the door.",
          "Companions are personally vetted, background-checked, and reference-verified, and they are introduced in person before any standing schedule begins. If the fit is not right, we change it quietly and at no additional cost.",
        ],
      },
      {
        heading: "Everyday life along the boulevard",
        paragraphs: [
          "A typical week includes a walk in Percy Warner Park or the level paths at Cheekwood, a standing appointment kept without a scramble, lunch made at home, and the club, church, or bridge table that anchors the calendar. Errands run along Harding Pike and through Belle Meade Plaza; the pharmacy and the grocery stop being a problem to solve.",
          "For households with staff already in place, our companion works alongside them rather than over them — the aim is a home that feels unchanged, with one more capable person in it.",
        ],
      },
      {
        heading: "Family coordination from a distance",
        paragraphs: [
          "Many of our Belle Meade engagements are arranged by adult children living elsewhere. They get a short written note after each visit, a direct line to the person managing the match, and an early warning when something looks different — a skipped meal, a new unsteadiness, a stack of unopened mail.",
          "We drive to Saint Thomas West, Vanderbilt, and the specialists in Green Hills and Midtown, and we stay through the appointment so nothing important gets lost between the exam room and the car.",
        ],
      },
      {
        heading: "When a household in 37205 starts needing help",
        paragraphs: [
          "The first call rarely follows a crisis. More often it follows a small accumulation: driving that has quietly stopped, a spouse who has become the full-time caregiver without ever agreeing to it, a house that is still immaculate but a refrigerator that is not. Belle Meade households are practiced at keeping up appearances, which is exactly why the change is easy to miss until it is not.",
          "We begin with a private conversation, not an assessment form — who your parent is, what a good day looks like, what they would be embarrassed to need help with. The care plan follows from that. Because we are non-medical, we also say plainly when something we are seeing belongs to a physician or a home health agency, and we help the family get there.",
        ],
      },
      {
        heading: "Belle Meade pricing",
        paragraphs: [PRICING_PARAGRAPH],
      },
    ],
    highlights: [
      "One matched companion with a single point of contact",
      "Transportation to Saint Thomas West, Vanderbilt, and Green Hills specialists",
      "Household coordination, errands, and cook-together kitchen time",
      "Walks at Percy Warner Park and Cheekwood outings",
      "Respite, weekend, and overnight support on request",
    ],
    landmarks: ["Harding Pike", "Percy Warner Park", "Cheekwood", "Belle Meade Plaza", "Green Hills"],
    responseTime: "Same-week introductions available across 37205.",
  },
  {
    slug: "bellevue",
    name: "Bellevue",
    zip: ["37221"],
    blurb: "Trusted concierge companion care for seniors in Bellevue, Nashville, TN.",
    metaTitle: "In-Home Companion Care in Bellevue, Nashville TN | Private Senior Care",
    metaDescription:
      "Private in-home companion care for seniors in Bellevue, Nashville TN — companionship, transportation, errands and respite. Non-medical. Private pay.",
    intro:
      "Kinship Care Collective serves Bellevue with non-medical companion care built around the neighborhood's easy pace — greenway walks, familiar errands, and a home someone plans to keep.",
    sections: [
      {
        heading: "Care in a neighborhood that likes to be outside",
        paragraphs: [
          "Bellevue's best asset for an older adult is the Harpeth River Greenway, and our companions use it. Level paths, shade, and benches make a daily walk realistic even when balance has changed — and a walk taken with company is a walk that actually happens.",
          "Closer to home, care looks like a shared lunch, laundry folded during conversation, and the small maintenance of an independent life: lights changed, mail sorted, the calendar kept. Bellevue is a neighborhood of long-held houses on quiet streets off Old Hickory Boulevard and Sawyer Brown Road, and most of the older adults we meet here have no intention of leaving them. The work is making that choice sustainable rather than stubborn.",
        ],
      },
      {
        heading: "Driving and errands along Highway 70",
        paragraphs: [
          "We handle the Kroger run on Highway 70 South, the pharmacy, Nashville West, and the appointments that stretch into Belle Meade and Midtown. Our companions stay through appointments and report back to the family in plain language the same day.",
          "Bellevue sits far enough west that a doctor's appointment downtown can eat an entire morning, and that distance is often the first thing to quietly go wrong: a follow-up postponed, then postponed again. When someone else is driving, waiting, and writing down what was said, appointments stop slipping. Closer runs — the Bellevue library, the bank, church on Sunday, a haircut, the standing lunch with a neighbor — matter just as much and are treated as care, not favors.",
        ],
      },
      {
        heading: "What a week looks like in 37221",
        paragraphs: [
          "Most Bellevue families start with one standing visit at a fixed day and hour. A companion arrives, the kettle goes on, and the visit has a shape: conversation first, then a greenway loop or a slow turn around Red Caboose Park, then a meal cooked together in the kitchen rather than delivered in a bag. Before leaving, the companion tidies what needs tidying and writes the family a short note about how the day actually went.",
          "From there it grows as life asks it to — a second visit when a spouse needs an afternoon free, weekend hours after a hospital stay, an overnight when a family travels. Nothing here is packaged in advance; it is built after we have met your household.",
        ],
      },
      {
        heading: "Respite and flexible coverage",
        paragraphs: [
          "Part-time, full-time, weekend, and overnight coverage are all available, and families frequently start with a single weekly visit before adding hours. Because we are non-medical, we complement home health and physicians rather than replacing them.",
          "Spouses caring for a partner in Bellevue are our most common respite call. Four unhurried hours — enough to leave the house without listening for a fall — does more for a caregiver's stamina than any advice we could offer. Adult children out of state use us differently: as reliable eyes in the house and a person who says something early when a meal gets skipped or the mail stacks up.",
        ],
      },
      {
        heading: "Bellevue pricing",
        paragraphs: [PRICING_PARAGRAPH],
      },
    ],
    highlights: [
      "Companionship and neighborhood outings to the Harpeth River Greenway",
      "Transportation to Nashville West and Belle Meade appointments",
      "Cook-together kitchen time and grocery runs on Highway 70",
      "Medication reminders and wellness check-ins",
      "Flexible part-time, full-time, and overnight care",
    ],
    landmarks: ["Harpeth River Greenway", "Nashville West", "Bellevue Park", "Highway 70 South"],
    responseTime: "Same-week placements available across 37221.",
  },
  {
    slug: "green-hills",
    name: "Green Hills",
    zip: ["37215", "37205"],
    blurb: "Concierge companion care for older adults in Green Hills, Nashville.",
    metaTitle: "In-Home Companion Care in Green Hills, Nashville TN | Private Senior Care",
    metaDescription:
      "Private in-home companion care for seniors in Green Hills, Nashville — companionship, driving to Vanderbilt and Saint Thomas, respite. Non-medical. Private pay.",
    intro:
      "Green Hills households tend to have long-standing routines and very little patience for disruption. Kinship Care Collective provides non-medical companion care that slips into that routine — the same companion, the same day, the same steady presence — rather than rearranging a life around a schedule someone else built.",
    sections: [
      {
        heading: "Care that respects a Green Hills routine",
        paragraphs: [
          "The standing hair appointment, the Thursday bridge game, the walk before the Hillsboro Pike traffic builds, the pharmacy that knows your name — these are not small things. They are the architecture of an independent life, and the fastest way for care to fail is to ignore them.",
          "We start with the calendar you already keep and place a companion inside it. Over a few weeks the arrangement stops feeling like care and starts feeling like a person who shows up on Tuesdays.",
        ],
      },
      {
        heading: "Driving, appointments, and the Hillsboro Pike run",
        paragraphs: [
          "We drive to Vanderbilt University Medical Center, Saint Thomas West, and the specialists clustered around Green Hills and Midtown. Our companions stay through the appointment, write down what was actually said, and send a short note to the family the same day.",
          "Errands look like the Green Hills Kroger, the Green Hills Library, church, the post office, and lunch with a friend who has also stopped driving. Getting out of the house on purpose is the point.",
        ],
      },
      {
        heading: "Respite for the adult child nearby",
        paragraphs: [
          "Many Green Hills families have one adult child doing everything because they happen to live closest. Respite is how that person stays well enough to keep going — four unhurried hours where someone reliable is in the house and the phone does not have to be watched.",
          "Every companion is personally vetted, background-checked, and reference-verified before entering a home, and matched to the household rather than dispatched to it.",
        ],
      },
      {
        heading: "Green Hills pricing",
        paragraphs: [PRICING_PARAGRAPH],
      },
    ],
    highlights: [
      "Companionship visits with a consistent, matched companion in 37215",
      "Transportation to Vanderbilt, Saint Thomas West, and Hillsboro Pike errands",
      "Medication reminders and steady daily routines",
      "Light housekeeping and cook-together kitchen time",
      "Respite for family caregivers, including weekends and overnights",
    ],
    landmarks: ["Hillsboro Pike", "Green Hills Library", "Radnor Lake", "Lipscomb", "Sequoyah Hills"],
    responseTime: "Same-week introductions are typical in 37215.",
  },
  {
    slug: "mount-juliet",
    name: "Mt. Juliet",
    zip: ["37122", "37138"],
    blurb: "Private-pay companion care for older adults in Mt. Juliet, TN.",
    metaTitle: "In-Home Companion Care in Mt. Juliet, TN | Private Senior Care",
    metaDescription:
      "Private in-home companion care for seniors in Mt. Juliet, TN — companionship, transportation to Nashville appointments, respite. Non-medical. Private pay.",
    intro:
      "Kinship Care Collective serves Mt. Juliet families who want dependable, non-medical support at home without an agency atmosphere arriving with it: company, transportation, and the quiet logistics that keep an independent life intact.",
    sections: [
      {
        heading: "A Mt. Juliet week, not a shift",
        paragraphs: [
          "Mt. Juliet households are spread out, and distance is usually the first problem care has to solve. A companion who arrives on the same day at the same hour turns a scattered week into a predictable one — a walk at Charlie Daniels Park, lunch made in the kitchen, the laundry folded while the coffee finishes.",
          "We hold the schedule week to week on purpose. Consistency is what makes an older adult accept help; a rotating roster of strangers is what makes them refuse it.",
        ],
      },
      {
        heading: "Driving into Nashville and around Providence",
        paragraphs: [
          "Appointments rarely stay local. We drive down I-40 to Vanderbilt and Saint Thomas, out to Summit Medical Center in Hermitage, and around Providence for the errands that used to take a whole afternoon.",
          "Our companions stay for the appointment and send a short, plain-language note afterward, so a family member an hour away — or a plane ride away — knows what the doctor said before dinner.",
        ],
      },
      {
        heading: "Respite, weekends, and overnights",
        paragraphs: [
          "A great many calls start with a spouse who has not left the house alone in weeks. Respite is maintenance, not luxury: four reliable hours buys an appointment of your own, a long lunch, or an afternoon that belongs to you.",
          "Weekend and overnight support is available for families recovering from a hospital stay or bridging a gap after a fall.",
        ],
      },
      {
        heading: "Mt. Juliet pricing",
        paragraphs: [PRICING_PARAGRAPH],
      },
    ],
    highlights: [
      "Companionship visits and daily check-ins across 37122",
      "Transportation to Nashville, Hermitage, and Providence errands",
      "Medication reminders and daily routines",
      "Light housekeeping and cook-together kitchen time",
      "Weekend, overnight, and post-hospital respite support",
    ],
    landmarks: ["Providence", "Charlie Daniels Park", "Old Hickory Lake", "Lebanon Road", "I-40"],
    responseTime: "Same-week placements available across 37122.",
  },
];

/**
 * Location-specific FAQs. These are deliberately different for every area so each
 * page answers questions a family in that particular place would actually ask.
 */
export const neighborhoodFaqs: Record<string, Array<{ q: string; a: string }>> = {
  nashville: [
    {
      q: "Which Nashville neighborhoods do you cover?",
      a: "We work across Davidson County, with the heaviest concentration in Green Hills, Belle Meade, Sylvan Park, 12 South, Germantown, East Nashville, Hillsboro Village, and Bellevue. If you are just outside those pockets, call and ask — we place case by case rather than drawing a hard boundary on a map.",
    },
    {
      q: "Can a companion drive my parent to Vanderbilt or Saint Thomas West?",
      a: "Yes. Transportation is included with our in-home companion care plans. A companion drives to the appointment in their own insured vehicle or yours, stays through the visit, and sends the family a short written note the same day summarizing what the physician actually said.",
    },
    {
      q: "My mother lives alone in Nashville and I live out of state. What do I actually receive?",
      a: "A written note after each visit, a direct line to the person who chose the companion, and an early call when something changes — a skipped meal, a new unsteadiness, unopened mail piling up. Out-of-state adult children are a large share of the families we serve in Nashville.",
    },
    {
      q: "Is this the same as home health care in Nashville?",
      a: "No. Home health is clinical and typically physician-ordered — nursing, wound care, therapy. We are non-medical companion care: company, driving, meals, reminders, and household rhythm. Many Nashville families use both at once, and we coordinate with your home health team rather than duplicating them.",
    },
  ],
  brentwood: [
    {
      q: "Do you cover all of Brentwood, including the neighborhoods off Concord and Split Log?",
      a: "Yes — all of 37027, from Maryland Farms and Crockett Park out to the Concord Road and Split Log corridors, and south into the Cool Springs edge of Brentwood.",
    },
    {
      q: "My father just stopped driving. Can you handle everything he used to do by car?",
      a: "That is one of the most common reasons Brentwood families call. Standing rides to Williamson Medical Center, Vanderbilt clinics up I-65, Cool Springs errands, church, the barber, and the grocery — arranged on a predictable weekly rhythm so it stops feeling like asking for a favor.",
    },
    {
      q: "We already have a housekeeper and a lawn service. How does a companion fit in?",
      a: "Alongside them, not over them. Our companion handles company, driving, meals, reminders, and the coordination between everyone else — the aim is a household that runs the way it already does, with one more capable person in it.",
    },
    {
      q: "Can we start with just a few hours a week?",
      a: "Most Brentwood families do. Our Essential plan includes one standing four-hour in-home visit each week; hours grow only when the family asks. Concierge is coordination only and does not include in-home visits. Current pricing for both is on our services page.",
    },
    {
      q: "Is home care in Brentwood the same as home health?",
      a: "No. Home health is clinical care ordered by a physician — nursing, wound care, therapy — and is usually short-term. Home care like ours is non-medical: companionship, transportation, meals, reminders, and household steadiness for as long as your family wants it. Many Brentwood households use both at once, and we coordinate with the home health team rather than duplicating it.",
    },
  ],
  franklin: [
    {
      q: "How far into Williamson County do you travel from Franklin?",
      a: "We routinely serve 37064, 37067, and 37069 — Downtown Franklin, Westhaven, Berry Farms, and the roads out toward Leiper's Fork. Farther out, we place case by case depending on companion availability.",
    },
    {
      q: "Do you provide overnight care in Franklin after a hospital discharge?",
      a: "Yes. Overnight and weekend support is included in the Plus and Premium packages, and it is most often used for the unsettled two or three weeks after a discharge from Williamson Medical Center or Vanderbilt when no one wants a parent alone at 2 a.m.",
    },
    {
      q: "Can a companion take my mother out rather than just sit with her?",
      a: "That is the point. Franklin rewards getting out — Pinkerton Park, a quiet coffee on Main Street before the sidewalks fill, The Factory, a drive toward Leiper's Fork in the fall. Daylight, movement, and conversation do more for a week than any checklist.",
    },
    {
      q: "Do you work with hospice in Franklin?",
      a: "We do, frequently. Hospice provides the clinical and spiritual care; we provide presence, household steadiness, and relief for the family in the hours hospice is not in the house. We never duplicate or contradict the hospice plan of care.",
    },
  ],
  "belle-meade": [
    {
      q: "How do you protect our family's privacy?",
      a: "One matched companion, one point of contact, no rotating roster, and no agency branding at the door. Companions are personally vetted, background-checked, and reference-verified, and they are introduced in person before any standing schedule begins.",
    },
    {
      q: "What if the companion is not the right fit for our household?",
      a: "We change the match quietly and at no additional cost. Fit is the whole premise of the practice — matched, never assigned — and a polite mismatch is a normal part of getting it right.",
    },
    {
      q: "Can a companion work alongside staff we already employ?",
      a: "Yes, and it is common in Belle Meade. Our companion coordinates with existing household staff rather than supervising them, so the home feels unchanged.",
    },
    {
      q: "Which appointments will you drive to from 37205?",
      a: "Saint Thomas West, Vanderbilt, and the specialists clustered in Green Hills and Midtown, plus errands along Harding Pike and Belle Meade Plaza. The companion stays through the appointment so nothing gets lost between the exam room and the car.",
    },
    {
      q: "How much does in-home companion care cost in Belle Meade?",
      a: "Monthly plans and hourly care are separate pricing options. Concierge covers coordination only; the in-home plans add one, two, or more standing visits each week. Current package pricing is listed on our services page, and we confirm every non-medical, private-pay schedule in writing after a private consultation.",
    },
    {
      q: "Do you provide nursing or medical care in Belle Meade?",
      a: "No. We are non-medical by design — companionship, driving, meals, household steadiness, and respite. We coordinate with physicians, home health, and hospice rather than replacing them, and we say plainly when something we are seeing belongs to a clinician.",
    },
    {
      q: "How soon can care begin in Belle Meade?",
      a: "Same-week introductions are typical across 37205. We meet the household first, propose a match, and only then set a standing schedule — the introduction is never a stranger arriving for a shift.",
    },
  ],
  bellevue: [
    {
      q: "Do you serve all of Bellevue and the 37221 area?",
      a: "Yes — the full 37221 footprint, including the neighborhoods along Old Hickory Boulevard, Highway 70 South, and out toward the Harpeth River.",
    },
    {
      q: "My husband needs to walk daily but I am afraid of a fall. Can a companion help?",
      a: "This is one of the best uses of Bellevue's geography. The Harpeth River Greenway is level and shaded with benches, and a companion walking alongside makes a daily loop realistic again. We are non-medical, so we provide steadying company and supervision rather than physical therapy.",
    },
    {
      q: "Can you handle groceries and pharmacy runs in Bellevue?",
      a: "Yes — the Kroger on Highway 70 South, the pharmacy, Nashville West, and appointments that stretch into Belle Meade and Midtown are all routine parts of a visit.",
    },
    {
      q: "Is weekend coverage available in Bellevue?",
      a: "Yes. Weekend visits and overnight coverage are part of the Plus and Premium packages, and many Bellevue families begin with one weekday visit and add a Saturday later.",
    },
    {
      q: "How much does in-home companion care cost in Bellevue?",
      a: "Monthly plans and hourly care are separate pricing options. Concierge covers coordination only; the in-home plans add one, two, or more standing visits each week. Current package pricing is listed on our services page, and we confirm every non-medical, private-pay schedule in writing after a private consultation.",
    },
    {
      q: "Do your companions actually live near Bellevue, or drive out from Nashville?",
      a: "We place companions who already live on the west side of town wherever possible. It is the difference between someone who arrives unhurried and someone who has just fought Highway 70 traffic for forty minutes — and it is why the same person can keep showing up week after week.",
    },
    {
      q: "Can you help after a hospital stay in Bellevue?",
      a: "Yes. Post-hospital support is one of our most common Bellevue requests: a few weeks of heavier weekend and overnight presence while the household finds its footing, then a step down to a lighter standing visit. We are non-medical, so we work alongside any home health or therapy orders rather than replacing them.",
    },
  ],
  "green-hills": [
    {
      q: "Will the same companion come every week in Green Hills?",
      a: "Yes. A Green Hills routine is built over decades, and a rotating roster undoes it. You get one matched companion on the same day at the same hour, with a named backup introduced in advance for holidays and illness.",
    },
    {
      q: "Can a companion drive to Vanderbilt and Saint Thomas from 37215?",
      a: "Yes — Vanderbilt, Saint Thomas West, and the specialists along Hillsboro Pike and in Midtown. Because Green Hills traffic is what it is, we build appointment days around it rather than pretending it is not there.",
    },
    {
      q: "Do you help with the Mall at Green Hills, the salon, and other standing appointments?",
      a: "Absolutely. The standing hair appointment, the bridge game, the pharmacy that knows your name — protecting those is the work, not a bonus on top of it.",
    },
    {
      q: "My parent insists they do not need help. How do you approach that?",
      a: "We introduce a companion as company and a ride rather than supervision, and we start small — one visit, low stakes, easy to decline. Most Green Hills clients who resisted the idea end up defending the arrangement within a month.",
    },
  ],
  "mount-juliet": [
    {
      q: "Do you drive into Mt. Juliet from Nashville, or are companions local?",
      a: "We place companions who live in or near 37122 wherever possible, so a Providence errand or a Charlie Daniels Park walk does not begin with a forty-minute commute.",
    },
    {
      q: "Can you drive to appointments in Nashville and Hermitage?",
      a: "Yes. Rides down Lebanon Road and I-40 into Hermitage and Nashville for specialists, plus local errands around Providence, are a standard part of care here.",
    },
    {
      q: "We need help only after a hospital stay. Is short-term care possible?",
      a: "Yes. Post-hospital bridging is a common Mt. Juliet request — several weeks of weekend and overnight support while the household finds its footing, then a step down to a lighter standing visit or nothing at all.",
    },
    {
      q: "How quickly can care start in Mt. Juliet?",
      a: "Same-week placements are typical across 37122, and we will attempt a next-day introduction for genuinely time-sensitive situations such as a discharge date that moved up.",
    },
  ],
};


/** Retired slugs kept so old links and search results resolve instead of 404ing. */
export const neighborhoodRedirects: Record<string, string> = {
  hendersonville: "nashville",
};

export const getNeighborhood = (slug: string) => neighborhoods.find((n) => n.slug === slug);

/**
 * Why a family in this specific city chooses a boutique practice over a franchise
 * agency. Distinct per city — displayed above the fold on each area page.
 */
export const whyConcierge: Record<string, string> = {
  nashville:
    "Nashville has no shortage of franchise home-care offices, and most of them fill a shift from a roster. Families here call us because they want to know the name of the person who will be sitting with their mother on Thursday — and know it will still be that person in March. We answer our own phone, we match on temperament before availability, and we keep the same companion in the home. Our services in Nashville include companion care, overnight respite, post-hospital support, personal care support, and care coordination alongside your medical team.",
  brentwood:
    "Brentwood families tend to have already handled the logistics — the doctors, the finances, the house. What they cannot outsource to a call center is judgment. A boutique practice means the person who takes your call also sets the care plan and hears about it directly if something is off, rather than a franchise coordinator two counties away. Our Brentwood services include companion care, overnight respite, post-hospital support, personal care support, and care coordination.",
  franklin:
    "In Franklin, care is often a family conversation held across a distance — a daughter in Nashville, a son out of state, a parent who does not want a stranger in the house. A small practice can be specific about who is coming, why they were chosen and what changes if it is not working. Franchises rarely can. Companion care, overnight respite, post-hospital support, personal care support and care coordination are all arranged here directly.",
  "belle-meade":
    "Discretion matters in Belle Meade, and it is difficult to promise when staffing rotates weekly. We keep the roster small deliberately: one consistent companion, one point of contact, no revolving introductions at the door. Services here include companion care, overnight respite, post-hospital support, personal care support, and coordination with the family's existing medical team.",
  bellevue:
    "Bellevue sits far enough west that franchise agencies treat it as the edge of the map — the companion sent out here is usually whoever was free and willing to make the drive, and it changes week to week. We do the opposite: we place companions who already live on this side of town, so a greenway walk or a Highway 70 errand does not start with a forty-minute commute, and the same person keeps showing up. Services in Bellevue include companion care, overnight respite, post-hospital support, personal care support, and care coordination alongside your medical team.",
  "green-hills":
    "Green Hills routines are built over decades, and a rotating roster undoes them in a month. Because we are small, we can match on temperament first and then protect the schedule around it — the same companion, the same day, the same hour, with a named backup introduced in advance rather than a stranger at the door. Services here include companion care, overnight respite, post-hospital support, personal care support, and coordination with your medical team.",
  "mount-juliet":
    "Most home-care offices serving Mt. Juliet are actually based in Nashville, which shows up as late arrivals and shifting faces. We place companions who live in or near 37122 and keep one point of contact who knows your household by name. Services include companion care, overnight respite, post-hospital support, personal care support, and care coordination.",
};

/**
 * The phrases families actually type into Google for each area. Used for on-page
 * keyword targeting (the SEO keywords list) — written the way people search,
 * not the way an agency writes.
 */
export const localSearchTerms: Record<string, string[]> = {
  nashville: [
    "in-home companion care Nashville TN",
    "senior care at home Nashville",
    "elderly companion services Nashville",
    "respite care for family caregivers Nashville",
    "non-medical home care Nashville TN",
    "caregiver for elderly parent Nashville",
    "senior transportation to Vanderbilt appointments",
  ],
  brentwood: [
    "in-home senior care Brentwood TN",
    "companion care 37027",
    "elderly care at home Brentwood Tennessee",
    "senior transportation Brentwood TN",
    "respite care Brentwood TN",
    "private caregiver for elderly parent Brentwood",
  ],
  franklin: [
    "in-home care Franklin TN",
    "senior companion care Franklin Tennessee",
    "overnight care for elderly Franklin TN",
    "respite care Williamson County",
    "non-medical home care 37064",
    "caregiver after hospital discharge Franklin TN",
  ],
  "belle-meade": [
    "private senior care Belle Meade TN",
    "in-home companion care 37205",
    "discreet elderly care Belle Meade Nashville",
    "private duty caregiver Belle Meade",
    "senior care near Harding Pike",
  ],
  bellevue: [
    "in-home care Bellevue Nashville TN",
    "senior companion care 37221",
    "elderly care at home Bellevue TN",
    "respite care Bellevue Nashville",
    "caregiver for mom Bellevue TN",
  ],
  "green-hills": [
    "senior care Green Hills Nashville",
    "in-home companion care 37215",
    "private caregiver Green Hills TN",
    "elderly transportation Hillsboro Pike",
    "respite care Green Hills Nashville",
  ],
  "mount-juliet": [
    "in-home senior care Mt Juliet TN",
    "companion care 37122",
    "elderly care at home Mount Juliet Tennessee",
    "respite care Mt Juliet TN",
    "caregiver for elderly parent Wilson County",
  ],
};

/**
 * Plain answers to the searches above, rendered as an on-page section so each
 * area page speaks the same language families use when they start looking.
 */
export const careSearchAnswers: Record<string, Array<{ q: string; a: string }>> = {
  nashville: [
    {
      q: "\u201cIn-home companion care near me\u201d in Nashville",
      a: "For most Nashville families this means a non-medical companion who comes to the house on a standing schedule — company, a ride, a meal cooked together, and a written note to the family afterward. We cover Davidson County, with the most families in Green Hills, Belle Meade, Sylvan Park, 12 South, East Nashville, and Bellevue.",
    },
    {
      q: "\u201cSenior care at home\u201d versus a facility",
      a: "Home care lets an older adult keep the house, the neighbors, and the routine. It works when the needs are companionship, driving, meals, reminders, and household steadiness. When the need becomes clinical, we say so and help the family reach home health, a physician, or the right level of care.",
    },
    {
      q: "\u201cRespite care for family caregivers\u201d in Nashville",
      a: "Respite is our most common first call: four unhurried hours where someone reliable is in the house so a spouse or adult child can leave without listening for a fall. Families usually start with one weekly block and add hours as the year gets heavier.",
    },
  ],
  brentwood: [
    {
      q: "\u201cIn-home senior care near me\u201d in 37027",
      a: "We cover all of Brentwood — Maryland Farms, Crockett Park, the Concord Road and Split Log corridors, and the Cool Springs edge. Care is non-medical: a matched companion on a standing weekly schedule rather than a rotating roster from a franchise office.",
    },
    {
      q: "\u201cSenior transportation Brentwood TN\u201d",
      a: "Standing rides to Williamson Medical Center, Vanderbilt clinics up I-65, Cool Springs errands, church, and the grocery. The companion stays through the appointment and sends the family a plain-language note the same day.",
    },
    {
      q: "\u201cPrivate caregiver for an elderly parent\u201d in Brentwood",
      a: "Private-pay, non-medical, and matched to the household rather than assigned. Every companion is personally vetted, background-checked, and reference-verified before entering a home, and introduced in person before any standing schedule begins.",
    },
  ],
  franklin: [
    {
      q: "\u201cIn-home care Franklin TN\u201d",
      a: "Non-medical companion care across 37064, 37067, and 37069 — Downtown Franklin, Westhaven, Berry Farms, and out toward Leiper's Fork. Visits are built around getting out of the house, not sitting in it.",
    },
    {
      q: "\u201cOvernight care for older adults\u201d in Williamson County",
      a: "Overnight and weekend coverage is available and is most often used for the unsettled weeks after a discharge from Williamson Medical Center or Vanderbilt, when no one wants a parent alone at 2 a.m.",
    },
    {
      q: "\u201cCare after a hospital discharge\u201d in Franklin",
      a: "We bridge the gap: heavier presence for a few weeks, then a step down to a lighter standing visit. Because we are non-medical, we work alongside home health and therapy orders rather than replacing them.",
    },
  ],
  "belle-meade": [
    {
      q: "\u201cPrivate senior care Belle Meade\u201d",
      a: "One matched companion, one point of contact, no agency branding at the door, and no rotating faces. Discretion is the service as much as the support is.",
    },
    {
      q: "\u201cIn-home companion care 37205\u201d",
      a: "Walks at Percy Warner Park or Cheekwood, errands along Harding Pike and Belle Meade Plaza, lunch made at home, and driving to Saint Thomas West, Vanderbilt, and Green Hills specialists.",
    },
    {
      q: "\u201cPrivate duty caregiver\u201d versus an agency",
      a: "A franchise fills a shift from a roster; we match a person to a household and keep them there. If the fit is not right, we change it quietly and at no additional cost.",
    },
  ],
  bellevue: [
    {
      q: "\u201cIn-home care Bellevue Nashville\u201d",
      a: "The full 37221 footprint — Old Hickory Boulevard, Highway 70 South, and out toward the Harpeth River. We place companions who already live on the west side, so visits start unhurried.",
    },
    {
      q: "\u201cCaregiver for mom\u201d in Bellevue",
      a: "Usually this starts as one standing weekly visit: conversation, a greenway loop or a turn around Red Caboose Park, a meal cooked together, and a short note to the family about how the day actually went.",
    },
    {
      q: "\u201cRespite care Bellevue\u201d",
      a: "Spouses caring for a partner are our most common Bellevue call. Four reliable hours a week does more for a caregiver's stamina than any advice we could offer, and weekend and overnight coverage is available when a household needs it.",
    },
  ],
  "green-hills": [
    {
      q: "\u201cSenior care Green Hills Nashville\u201d",
      a: "A matched companion who slips into a routine built over decades — the standing hair appointment, the bridge game, the walk before Hillsboro Pike traffic builds — rather than rearranging a life around someone else's schedule.",
    },
    {
      q: "\u201cElderly transportation\u201d around 37215",
      a: "Vanderbilt, Saint Thomas West, and the specialists along Hillsboro Pike and Midtown, plus the Green Hills Kroger, the library, church, and lunch with a friend who has also stopped driving.",
    },
    {
      q: "\u201cPrivate caregiver Green Hills\u201d",
      a: "Non-medical, private-pay, same person every week, with a named backup introduced in advance for holidays and illness rather than a stranger at the door.",
    },
  ],
  "mount-juliet": [
    {
      q: "\u201cIn-home senior care Mt Juliet TN\u201d",
      a: "Companion care across 37122 and the Providence area, with companions who live in or near Mt. Juliet so a park walk or an errand does not begin with a forty-minute commute.",
    },
    {
      q: "\u201cRides to Nashville appointments\u201d from Mt. Juliet",
      a: "Down I-40 to Vanderbilt and Saint Thomas, out to Summit Medical Center in Hermitage, and around Providence for local errands. The companion stays for the appointment and writes the family afterward.",
    },
    {
      q: "\u201cShort-term care after a hospital stay\u201d in Mt. Juliet",
      a: "Common here. Several weeks of weekend and overnight support while the household finds its footing, then a step down to a lighter standing visit — or nothing at all.",
    },
  ],
};
